/**
 * Sober inline SVG banners for the "Situations traitées" section.
 * One coherent visual universe: pale background, slate strokes,
 * navy/forest accents — no icons, no people, no anxious imagery.
 *
 * Viewbox is 800×240 (10:3 ratio) so each banner renders as a
 * short horizontal band that supports the editorial text without
 * dominating it.
 */

const PALETTE = {
    bg: "#F2F1ED",
    bgDark: "#E5E4DE",
    line: "#94A3B8",
    lineSoft: "#CBD5E1",
    accent: "#0B132B",
    accentSoft: "#1B4332",
};

function BannerFrame({ children }) {
    return (
        <svg
            viewBox="0 0 800 240"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid slice"
            className="block w-full h-full"
            role="img"
            aria-hidden="true"
        >
            <rect width="800" height="240" fill={PALETTE.bg} />
            {children}
        </svg>
    );
}

/* Card 1 — Collecte de données : champs de formulaire abstraits */
function BannerForm() {
    return (
        <BannerFrame>
            {/* Field 1 */}
            <rect x="80" y="60" width="180" height="14" rx="2" fill={PALETTE.lineSoft} />
            <rect x="80" y="86" width="260" height="22" rx="3" fill="none" stroke={PALETTE.line} strokeWidth="1" />
            {/* Field 2 */}
            <rect x="80" y="128" width="140" height="14" rx="2" fill={PALETTE.lineSoft} />
            <rect x="80" y="154" width="320" height="22" rx="3" fill="none" stroke={PALETTE.line} strokeWidth="1" />
            {/* Right column: subtler ghost field */}
            <rect x="440" y="60" width="200" height="14" rx="2" fill={PALETTE.bgDark} />
            <rect x="440" y="86" width="280" height="22" rx="3" fill="none" stroke={PALETTE.lineSoft} strokeWidth="1" />
            <rect x="440" y="128" width="120" height="14" rx="2" fill={PALETTE.bgDark} />
            <rect x="440" y="154" width="240" height="22" rx="3" fill="none" stroke={PALETTE.lineSoft} strokeWidth="1" />
            {/* Accent mark */}
            <rect x="80" y="194" width="40" height="3" rx="1" fill={PALETTE.accent} />
        </BannerFrame>
    );
}

/* Card 2 — Pratiques numériques : flux calmes entre points */
function BannerFlows() {
    return (
        <BannerFrame>
            {/* Subtle curves connecting nodes */}
            <path d="M 100 170 C 220 120, 300 80, 420 110 S 620 200, 720 130"
                fill="none" stroke={PALETTE.line} strokeWidth="1" />
            <path d="M 100 90 C 240 130, 340 180, 480 150 S 640 90, 720 170"
                fill="none" stroke={PALETTE.lineSoft} strokeWidth="1" />
            {/* Nodes */}
            <circle cx="100" cy="170" r="5" fill={PALETTE.accent} />
            <circle cx="100" cy="90" r="4" fill={PALETTE.lineSoft} />
            <circle cx="420" cy="110" r="4" fill={PALETTE.line} />
            <circle cx="480" cy="150" r="4" fill={PALETTE.line} />
            <circle cx="720" cy="130" r="5" fill={PALETTE.accentSoft} />
            <circle cx="720" cy="170" r="4" fill={PALETTE.lineSoft} />
            <circle cx="300" cy="80" r="3" fill={PALETTE.lineSoft} />
            <circle cx="620" cy="200" r="3" fill={PALETTE.lineSoft} />
            <circle cx="340" cy="180" r="3" fill={PALETTE.lineSoft} />
            <circle cx="640" cy="90" r="3" fill={PALETTE.lineSoft} />
        </BannerFrame>
    );
}

/* Card 3 — Conformité : repères et marges documentaires */
function BannerDocument() {
    return (
        <BannerFrame>
            {/* Left margin rule */}
            <line x1="120" y1="40" x2="120" y2="200" stroke={PALETTE.line} strokeWidth="1" />
            {/* Section marker dots on margin */}
            <circle cx="120" cy="70" r="3" fill={PALETTE.accent} />
            <circle cx="120" cy="120" r="3" fill={PALETTE.line} />
            <circle cx="120" cy="170" r="3" fill={PALETTE.line} />
            {/* Content lines (varying lengths) */}
            <rect x="150" y="66" width="380" height="2" fill={PALETTE.lineSoft} />
            <rect x="150" y="78" width="320" height="2" fill={PALETTE.lineSoft} />
            <rect x="150" y="90" width="260" height="2" fill={PALETTE.lineSoft} />
            <rect x="150" y="116" width="420" height="2" fill={PALETTE.line} />
            <rect x="150" y="128" width="360" height="2" fill={PALETTE.lineSoft} />
            <rect x="150" y="140" width="300" height="2" fill={PALETTE.lineSoft} />
            <rect x="150" y="166" width="340" height="2" fill={PALETTE.lineSoft} />
            <rect x="150" y="178" width="260" height="2" fill={PALETTE.lineSoft} />
            {/* Right edge rule (margin) */}
            <line x1="680" y1="40" x2="680" y2="200" stroke={PALETTE.lineSoft} strokeWidth="1" />
        </BannerFrame>
    );
}

/* Card 4 — Obligations courantes : registre sobre, repères et lignes documentaires */
function BannerRegister() {
    /* Three grouped "register entries", each prefixed by a small accent marker
       and followed by short documentary lines. Reads as a sober compliance log
       without using icons or checkmarks. */
    const entries = [60, 120, 180];
    return (
        <BannerFrame>
            {entries.map((y, i) => (
                <g key={y}>
                    {/* Left accent marker (filled square) */}
                    <rect
                        x="90"
                        y={y - 6}
                        width="12"
                        height="12"
                        rx="1"
                        fill={i === 0 ? PALETTE.accent : PALETTE.line}
                    />
                    {/* Primary line (heading) */}
                    <rect x="120" y={y - 5} width="220" height="3" fill={PALETTE.line} />
                    {/* Secondary lines */}
                    <rect x="120" y={y + 8} width="380" height="2" fill={PALETTE.lineSoft} />
                    <rect x="120" y={y + 20} width="300" height="2" fill={PALETTE.lineSoft} />
                    {/* Right column small marker */}
                    <rect x="640" y={y - 5} width="60" height="3" fill={PALETTE.lineSoft} />
                    {/* Divider below (except last) */}
                    {i < entries.length - 1 && (
                        <line
                            x1="80"
                            y1={y + 36}
                            x2="720"
                            y2={y + 36}
                            stroke={PALETTE.lineSoft}
                            strokeWidth="1"
                        />
                    )}
                </g>
            ))}
        </BannerFrame>
    );
}

const BANNERS = {
    form: BannerForm,
    flows: BannerFlows,
    document: BannerDocument,
    register: BannerRegister,
};

export function SituationBanner({ kind }) {
    const Component = BANNERS[kind];
    return Component ? <Component /> : null;
}
